import { useRef, useState } from 'react';
import { api, type Conto } from '../api';
import { Campo, Caricamento, Etichetta, Importo, Scheda, Vuoto, useAvvisi, useCarico } from '../componenti/base';
import { formattaData, formattaEuro, plurale } from '../formato';
import { useSessione } from '../main';

interface Mappatura {
  data: number | null;
  valuta: number | null;
  dicitura: number | null;
  importo: number | null;
  dare: number | null;
  avere: number | null;
  primaRigaDati: number;
  /** Giorno/mese/anno o mese/giorno/anno: lo riconosce il server dalle date del file. */
  ordineData: 'GMA' | 'MGA';
  /** Acquisti positivi, come negli estratti delle carte. */
  segnoInvertito: boolean;
}

interface Anteprima {
  nomeFile: string;
  formato: 'CSV' | 'EXCEL' | 'HTML' | 'CBI';
  avvisi: string[];
  mappatura: Mappatura;
  mancanti: string[];
  campione: string[][];
  movimenti: {
    riga: number;
    data: string;
    valuta: string | null;
    importoCent: number;
    dicitura: string;
    chiave: string;
    giaPresente: boolean;
    daCompletare: boolean;
  }[];
  scartate: { riga: number; contenuto: string; motivo: string }[];
  totali: {
    nuovi: number;
    giaPresenti: number;
    /** Già presenti ma senza descrizione: reimportando la si completa. */
    daCompletare: number;
    scartate: number;
    entrateCent: number;
    usciteCent: number;
  };
}

const CAMPI: {
  chiave: keyof Omit<Mappatura, 'primaRigaDati' | 'ordineData' | 'segnoInvertito'>;
  nome: string;
}[] = [
  { chiave: 'data', nome: 'Data' },
  { chiave: 'valuta', nome: 'Data valuta' },
  { chiave: 'dicitura', nome: 'Descrizione' },
  { chiave: 'importo', nome: 'Importo (colonna unica)' },
  { chiave: 'dare', nome: 'Uscite / Dare' },
  { chiave: 'avere', nome: 'Entrate / Avere' },
];

export function ImportMovimenti({ onImportato }: { onImportato?: () => void }) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const input = useRef<HTMLInputElement>(null);
  const conti = useCarico(() => api.get<{ conti: Conto[] }>('/api/conti'), []);
  const [contoId, setContoId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null);
  const [attesa, setAttesa] = useState(false);

  const leggi = async (f: File, mappatura?: Partial<Mappatura>) => {
    if (!contoId) {
      avvisa('Scegli prima il conto: senza, non si sa dove finirebbero i movimenti.', 'errore');
      return;
    }
    setAttesa(true);
    try {
      const form = new FormData();
      form.append('contoId', contoId);
      if (mappatura) form.append('mappatura', JSON.stringify(mappatura));
      form.append('file', f);
      const a = await api.carica<Anteprima>('/api/importa/movimenti/anteprima', form);
      setAnteprima(a);
      setFile(f);
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Lettura non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const conferma = async () => {
    if (!anteprima || !file) return;
    setAttesa(true);
    try {
      const r = await api.post<{
        inseriti: number;
        giaPresenti: number;
        scartate: number;
        regole: { proposte: number };
      }>('/api/importa/movimenti/conferma', {
        contoId,
        nomeFile: anteprima.nomeFile,
        anteprima,
      });
      avvisa(
        `${plurale(r.inseriti, 'movimento importato', 'movimenti importati')}` +
          (r.regole.proposte ? `, ${r.regole.proposte} con la voce già proposta dalle regole.` : '.'),
      );
      setAnteprima(null);
      setFile(null);
      if (input.current) input.current.value = '';
      conti.ricarica();
      onImportato?.();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Import non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  // Il numero di colonne è quello della riga **più larga**, non della prima:
  // sopra l'intestazione ci sono spesso righe con una cella sola (il nome della
  // banca, l'IBAN), e contare quelle farebbe sparire dal menu tutte le colonne
  // tranne la prima.
  const colonne = (anteprima?.campione ?? []).reduce((max, r) => Math.max(max, r.length), 0);

  // I nomi di colonna da mostrare nel menu vengono dalla riga riconosciuta come
  // intestazione; se non ce n'è una, restano i numeri.
  const intestazione: string[] =
    anteprima && anteprima.mappatura.primaRigaDati > 0
      ? (anteprima.campione[anteprima.mappatura.primaRigaDati - 1] ?? [])
      : [];

  return (
    <div className="space-y-5">
      <Scheda
        titolo="1. Scegli il conto e il file"
        sottotitolo="Il file non viene scritto subito: prima si guarda cosa è stato capito."
      >
        <div className="flex flex-wrap items-end gap-3">
          <Campo etichetta="Conto" larghezza="w-72">
            <select className="campo" value={contoId} onChange={(e) => setContoId(e.target.value)}>
              <option value="">Scegli…</option>
              {(conti.dati?.conti ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                  {c.dataUltimoImport
                    ? ` — ultimo movimento ${formattaData(c.dataUltimoImport, organizzazione.formatoData)}`
                    : ' — mai importato'}
                </option>
              ))}
            </select>
          </Campo>
          <button className="bottone-primario" disabled={!contoId || attesa} onClick={() => input.current?.click()}>
            {attesa ? 'Lettura…' : 'Scegli il file'}
          </button>
          <input
            ref={input}
            type="file"
            accept=".csv,.txt,.xlsx,.xls,.xml,text/xml,application/xml"
            hidden
            onChange={(e) => e.target.files?.[0] && leggi(e.target.files[0])}
          />
        </div>
        <p className="mt-3 text-xs text-inchiostro-500">
          <strong>CBI/CAMT.053</strong> (l’XML della banca), CSV o Excel: prendi quello che la banca ti dà. Se puoi
          scegliere, scegli il CBI — gli importi hanno già il segno e la deduplica usa il riferimento della banca. Sui
          formati a colonne Mastro riconosce da solo il separatore, le colonne e se l’importo è su una colonna sola col
          segno o su due Dare/Avere. Quello che non riconosce lo dice, invece di indovinare.
        </p>
      </Scheda>

      {anteprima && (
        <>
          <Scheda
            titolo="2. Controlla cosa è stato letto"
            sottotitolo={`${anteprima.nomeFile} · ${anteprima.formato}`}
            azioni={
              <button className="bottone-secondario" onClick={() => setAnteprima(null)}>
                Annulla
              </button>
            }
          >
            {anteprima.avvisi.length > 0 && (
              <ul className="mb-4 space-y-1 rounded-md border border-attenzione/30 bg-attenzione-chiaro p-3 text-sm text-attenzione">
                {anteprima.avvisi.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            )}

            {anteprima.formato === 'CBI' && (
              <p className="text-sm text-inchiostro-500">
                Formato <strong>CBI/CAMT.053</strong>: le colonne non si mappano perché il file le dichiara. Gli importi
                arrivano già col segno, le date contabile e valuta sono distinte, e la deduplica usa il riferimento
                univoco della banca invece di data e importo.
              </p>
            )}

            {anteprima.formato !== 'CBI' && anteprima.mancanti.length > 0 && (
              <div className="mb-4 rounded-md border border-attenzione/30 bg-attenzione-chiaro p-3 text-sm text-attenzione">
                Non ho riconosciuto: <strong>{anteprima.mancanti.join(', ')}</strong>. Indica tu le colonne qui sotto.
              </div>
            )}

            {anteprima.formato !== 'CBI' && (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {CAMPI.map((c) => (
                    <Campo key={c.chiave} etichetta={c.nome}>
                      <select
                        className="campo"
                        value={anteprima.mappatura[c.chiave] ?? ''}
                        onChange={(e) =>
                          file &&
                          leggi(file, {
                            ...anteprima.mappatura,
                            [c.chiave]: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      >
                        <option value="">— non c’è —</option>
                        {Array.from({ length: colonne }, (_, i) => (
                          <option key={i} value={i}>
                            Colonna {i + 1}
                            {intestazione[i] ? ` — ${intestazione[i]}` : ''}
                          </option>
                        ))}
                      </select>
                    </Campo>
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap items-end gap-4">
                  <Campo etichetta="Formato delle date" larghezza="w-72">
                    <select
                      className="campo"
                      value={anteprima.mappatura.ordineData}
                      onChange={(e) =>
                        file &&
                        leggi(file, { ...anteprima.mappatura, ordineData: e.target.value === 'MGA' ? 'MGA' : 'GMA' })
                      }
                    >
                      <option value="GMA">giorno/mese/anno — 25/03/2026</option>
                      <option value="MGA">mese/giorno/anno — 03/25/2026</option>
                    </select>
                  </Campo>
                  <label className="flex items-center gap-2 pb-2 text-sm">
                    <input
                      type="checkbox"
                      checked={anteprima.mappatura.segnoInvertito}
                      onChange={(e) => file && leggi(file, { ...anteprima.mappatura, segnoInvertito: e.target.checked })}
                    />
                    Segno invertito: nel file gli acquisti sono positivi, come negli estratti delle carte
                  </label>
                </div>

                <div className="mt-4 overflow-x-auto rounded-md border border-inchiostro-200">
                  <table className="dati">
                    <tbody>
                      {anteprima.campione.map((r, i) => (
                        <tr
                          key={i}
                          className={i === anteprima.mappatura.primaRigaDati - 1 ? 'bg-inchiostro-100 font-medium' : ''}
                        >
                          {r.map((c, j) => (
                            <td key={j} className="whitespace-nowrap text-xs">
                              {c || <span className="text-inchiostro-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-inchiostro-500">
                  Le prime righe del file. In grigio quella riconosciuta come intestazione.
                </p>
              </>
            )}
          </Scheda>

          <div className="grid gap-4 sm:grid-cols-4">
            <Riquadro titolo="Da importare" valore={String(anteprima.totali.nuovi)} />
            <Riquadro
              titolo="Già presenti"
              valore={String(anteprima.totali.giaPresenti)}
              nota="non verranno reinseriti"
            />
            <Riquadro titolo="Entrate" valore={formattaEuro(anteprima.totali.entrateCent)} tono="incassi" />
            <Riquadro titolo="Uscite" valore={formattaEuro(anteprima.totali.usciteCent)} tono="uscite" />
          </div>

          <Scheda titolo="Movimenti letti" senzaPadding>
            {anteprima.movimenti.length === 0 ? (
              <Vuoto titolo="Nessun movimento riconosciuto" testo="Controlla la mappatura delle colonne qui sopra." />
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="dati">
                  <thead>
                    <tr>
                      <th className="w-16">Riga</th>
                      <th>Data</th>
                      <th>Causale</th>
                      <th className="text-right">Importo</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {anteprima.movimenti.map((m) => (
                      <tr key={m.chiave} className={m.giaPresente ? 'opacity-50' : ''}>
                        <td className="num text-inchiostro-400">{m.riga}</td>
                        <td className="num whitespace-nowrap">{formattaData(m.data, organizzazione.formatoData)}</td>
                        <td className="max-w-96 truncate" title={m.dicitura}>
                          {m.dicitura}
                        </td>
                        <td className="text-right">
                          <Importo cent={m.importoCent} tono="auto" />
                        </td>
                        <td>{m.giaPresente && <Etichetta testo="già presente" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Scheda>

          {anteprima.scartate.length > 0 && (
            <Scheda
              titolo={`${plurale(anteprima.scartate.length, 'riga scartata', 'righe scartate')}`}
              sottotitolo="Non erano movimenti. Ognuna col motivo e il numero di riga del file, per poter andare a guardare."
              senzaPadding
            >
              <table className="dati">
                <thead>
                  <tr>
                    <th className="w-16">Riga</th>
                    <th>Motivo</th>
                    <th>Contenuto</th>
                  </tr>
                </thead>
                <tbody>
                  {anteprima.scartate.map((s, i) => (
                    <tr key={i}>
                      <td className="num text-inchiostro-400">{s.riga}</td>
                      <td className="text-attenzione">{s.motivo}</td>
                      <td className="max-w-96 truncate font-mono text-xs text-inchiostro-500" title={s.contenuto}>
                        {s.contenuto}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Scheda>
          )}

          <div className="flex items-center justify-between gap-3 rounded-lg border border-inchiostro-200 bg-white p-4">
            <p className="text-sm text-inchiostro-600">
              {anteprima.totali.nuovi > 0 ? (
                <>
                  Verranno inseriti <strong className="num">{anteprima.totali.nuovi}</strong> movimenti, senza voce
                  assegnata. Le regole automatiche proporranno subito quelle che riconoscono.
                </>
              ) : anteprima.totali.daCompletare > 0 ? (
                <>
                  Nessun movimento nuovo: ci sono già tutti. Verranno però completate{' '}
                  <strong>{plurale(anteprima.totali.daCompletare, 'descrizione rimasta vuota', 'descrizioni rimaste vuote')}</strong>.
                  Quelle già presenti, e quelle che hai corretto a mano, non vengono toccate.
                </>
              ) : (
                <>Nessun movimento nuovo e nessuna descrizione da completare: questo file è già stato importato tutto.</>
              )}
            </p>
            <button
              className="bottone-primario"
              onClick={conferma}
              disabled={attesa || (anteprima.totali.nuovi === 0 && anteprima.totali.daCompletare === 0)}
            >
              {attesa ? 'Import…' : anteprima.totali.nuovi === 0 ? 'Completa le descrizioni' : 'Conferma e importa'}
            </button>
          </div>
        </>
      )}

      {conti.caricamento && <Caricamento />}
    </div>
  );
}

function Riquadro({
  titolo,
  valore,
  nota,
  tono,
}: {
  titolo: string;
  valore: string;
  nota?: string;
  tono?: 'incassi' | 'uscite';
}) {
  return (
    <div className="scheda p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">{titolo}</p>
      <p
        className={`num mt-1 text-xl font-semibold ${tono === 'incassi' ? 'text-incassi' : tono === 'uscite' ? 'text-uscite' : ''}`}
      >
        {valore}
      </p>
      {nota && <p className="mt-0.5 text-xs text-inchiostro-500">{nota}</p>}
    </div>
  );
}
